import React, { useState, useCallback, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { FaSpinner } from "react-icons/fa";

import { useValidatePin } from "../../../services/queries/users";
import { useCreateWithdrawal } from "../../../services/queries/wallets";
import TextInput from "../../../components/TextInput";
import BackButton from "../../../components/BackButton";
import { showToast } from "../../../utils/toast";
import { useTransaction } from "../../../contexts/TransactionContext";
import { buildWithdrawalRequest } from "../../../utils/wallet";
import { useGetEncryptionKey } from "../../../services/queries/encryption";
import { LOGOUT, useAppState } from "../../../contexts/AppContext";
import { handleApiResponseError } from "../../../utils";
import { routePaths } from "../../../constants";
import { requireRouteAccess } from "../../../utils/authorization";

export const Route = createFileRoute("/_app/send/confirm")({
  beforeLoad: () => requireRouteAccess("/send/confirm"),
  component: ConfirmWithdrawal,
});

function ConfirmWithdrawal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { state: formData } = useTransaction();
  const { walletId } = formData;

  const { resetTransaction } = useTransaction();
  const {
    state: { user },
    dispatch: appDispatch,
  } = useAppState();

  const { publicKey } = useGetEncryptionKey();
  const { mutate: validatePin, isLoading: isValidatingPin } = useValidatePin();
  const { mutate: initiateWithdrawal, isLoading: isInitiatingWithdrawal } =
    useCreateWithdrawal();

  const [pin, setPin] = useState("");
  const [formError, setFormError] = useState("");

  const isSubmitting = isValidatingPin || isInitiatingWithdrawal;

  useEffect(() => {
    if (!user) {
      navigate({ to: routePaths.login });
    }
  }, [user, navigate]);

  const handlePinValidation = () => {
    setFormError("");

    if (pin.length !== 4) {
      setFormError("PIN must be exactly 4 digits.");
      return;
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      queryClient.removeQueries();
      localStorage.clear();
      appDispatch({ type: LOGOUT });
    } catch {
      showToast("Failed to log out. Please try again.", "error");
    }
  }, [queryClient, appDispatch]);

  const handleTransactionPinSubmitError = (error: unknown) => {
    if (error instanceof AxiosError) {
      const response = error.response?.data;

      if (!response) {
        return;
      }

      const { message, data } = response;

      if (data?.isLocked) {
        showToast(message, "error");
        resetTransaction();
        handleLogout();
        return;
      }

      if (data?.attemptsLeft) {
        showToast(
          `Invalid PIN. ${data.attemptsLeft} attempt${
            data.attemptsLeft === 1 ? "" : "s"
          } left.`,
          "error",
        );
        setFormError(message);
        return;
      }

      showToast(message || "Something went wrong. Please try again.", "error");
      setFormError(message);
      return;
    }

    if (error instanceof Error) {
      showToast(error.message, "error");
      setFormError(error.message);
      return;
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormError("");
    const val = e.target.value;

    if (/^\d*$/.test(val) && val.length <= 4) {
      setPin(val);
    }
  };

  const handleInitiateWithdrawalSuccess = () => {
    resetTransaction();
    navigate({ to: routePaths.sendSuccess });
  };

  const handleTransactionPinValidationSuccess = (): void => {
    if (!publicKey) return;
    const request = buildWithdrawalRequest(publicKey, {
      ...formData,
      pin,
    });

    initiateWithdrawal(
      { data: request, walletId },
      {
        onSuccess: () => {
          handleInitiateWithdrawalSuccess();
        },
        onError: (error) => {
          handleApiResponseError(error);
        },
      },
    );
  };

  const handleTransactionPinSubmit = (): void => {
    setFormError("");
    validatePin(
      { pin },
      {
        onSuccess: () => {
          handleTransactionPinValidationSuccess();
        },
        onError: (error: unknown) => {
          handleTransactionPinSubmitError(error);
        },
      },
    );
  };

  const isDisabled = isSubmitting || formError !== "" || pin.length !== 4;

  return (
    <div className="withdrawal-confirmation-page bg-light min-h-screen text-gray-800 p-4">
      <div className="flex items-center mb-6">
        <BackButton />
        <h1 className="text-xl font-bold ml-10">Confirm Withdrawal</h1>
      </div>

      <div className="px-4 py-6">
        <div className="disclaimer-container bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 px-4 py-3 rounded-md mb-4 flex items-center">
          <ExclamationCircleIcon className="w-5 h-5 text-yellow-500 mr-2" />
          <p className="text-sm">
            You have up to <strong>3 attempts</strong> to enter the correct PIN.
            After 3 failed attempts, your account will be{" "}
            <strong>temporarily locked</strong> for security reasons.
          </p>
        </div>

        {formError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {formError}
          </div>
        )}

        <div className="mb-6">
          <label className="block mb-1 text-sm">Enter PIN</label>
          <div className="relative">
            <TextInput
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={handlePinChange}
              onBlur={handlePinValidation}
              placeholder="Enter 4-digit PIN"
              className="bg-white text-gray-800 w-full p-3 rounded-md focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="px-4 py-4">
        <button
          onClick={handleTransactionPinSubmit}
          disabled={isDisabled}
          className={`bg-primary w-full py-3 rounded-md text-white font-semibold ${
            isDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? (
            <FaSpinner className="animate-spin inline" />
          ) : (
            "Confirm Transaction"
          )}
        </button>
      </div>
    </div>
  );
}

export default ConfirmWithdrawal;
